import { getOperationName } from '@apollo/client/utilities';
import { useCallback } from 'react';
import { useScratchpadPlaylistContext } from '../../components/machine/playlist/scratchpad-playlist.hooks';
import {
  trackRemoveScratchpad,
  trackRestoreScratchpad,
} from '../../config/analytics';
import { logger } from '../../util/logger';
import {
  ListScratchpadsDocument,
  Scratchpad,
  useGetScratchpadQuery,
  useRemoveScratchpadMutation,
  useRestoreScratchpadMutation,
} from '../graphql/generated';

interface ScratchpadUtilsResults {
  remove: (scratchpadId: string) => Promise<Scratchpad | undefined>;
  restore: (scratchpadId: string) => Promise<Scratchpad | undefined>;
}

const useScratchpadUtils = (): ScratchpadUtilsResults => {
  const listQueryName = getOperationName(ListScratchpadsDocument);

  const [removeScratchpad] = useRemoveScratchpadMutation({
    refetchQueries: listQueryName ? [listQueryName] : undefined,
  });

  const [restoreScratchpad] = useRestoreScratchpadMutation({
    refetchQueries: listQueryName ? [listQueryName] : undefined,
  });

  const remove = useCallback(
    async (scratchpadId: string): Promise<Scratchpad | undefined> => {
      try {
        const success = await removeScratchpad({
          variables: {
            input: {
              id: scratchpadId,
            },
          },
        });
        trackRemoveScratchpad();
        return success.data?.removeScratchpad;
      } catch (error) {
        logger.error('failed to remove scratchpad', { error, scratchpadId });
      }
    },
    [removeScratchpad]
  );

  const restore = useCallback(
    async (scratchpadId: string): Promise<Scratchpad | undefined> => {
      try {
        const success = await restoreScratchpad({
          variables: {
            input: {
              id: scratchpadId,
            },
          },
        });
        trackRestoreScratchpad();
        return success.data?.restoreScratchpad;
      } catch (error) {
        logger.error('failed to restore scratchpad', { error, scratchpadId });
      }
    },
    [restoreScratchpad]
  );

  return {
    remove,
    restore,
  };
};

const useLoadedScratchpad = (): Scratchpad | undefined => {
  const { statuses } = useScratchpadPlaylistContext();

  const activeScratchpadId = statuses?.find((s) => s.isLoaded)?.scratchpadId;

  const { data } = useGetScratchpadQuery({
    variables: { input: { id: activeScratchpadId as string } },
    skip: activeScratchpadId === undefined,
  });

  return data?.getScratchpad;
};

export { useScratchpadUtils, useLoadedScratchpad };
